/**
 * NestedList component for hierarchical navigation with indicators
 * Migrated from front/src/components/NestedList.js
 */

import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { getIndicatorNavigation } from '../config/settings';
import useTokenReplacement from '../hooks/ui/useTokenReplacement';
import NestedListItem from './NestedListItem';

/**
 * NestedList component for displaying hierarchical navigation
 * @param {Object} props - Component props
 * @param {Array} props.groups - Groups of navigation items
 * @param {Function} props.scrollTo - Function to scroll to element
 * @param {string} props.entityType - Type of entity for token replacement
 * @returns {JSX.Element} NestedList component
 */
const NestedList = ({
  groups = [],
  scrollTo,
  entityType,
}) => {
  const replaceTokens = useTokenReplacement({ entityType });

  return (
    <List>
      {groups.map(({ navTitle: title, key, items }) => {
        const hasItems = items.length > 0;

        return (
          <React.Fragment key={`nav-${key}`}>
            <ListItem
              button
              onClick={() => scrollTo(key)}
              sx={{ pt: 1, cursor: 'pointer' }}
            >
              <ListItemText primary={replaceTokens(title)} />
            </ListItem>

            {hasItems && (
              <>
                {items.map(({ key: itemKey, isIndicator, navTitle: itemNavTitle }) => {
                  // Get navigation title from indicatorsContent for indicators
                  // or use the navTitle from the item itself for non-indicators
                  const itemTitle = isIndicator
                    ? getIndicatorNavigation(itemKey)
                    : itemNavTitle;

                  return (
                    <NestedListItem
                      key={`nested-list-item-${itemKey}`}
                      itemTitle={itemTitle}
                      itemKey={itemKey}
                      isIndicator={isIndicator}
                      scrollTo={scrollTo}
                    />
                  );
                })}
              </>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

NestedList.displayName = 'NestedList';

export default NestedList;
